import { useNavigate } from 'react-router-dom';

import {
  addHttpIfMissing,
  AppIcon,
  AppIcons,
  appStrings as commonAppStrings,
  Employer,
  getInternshipRoleLabel,
  Internship,
  NuggetBadge,
  NuggetBadgeProps,
  internshipTransform,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { TheScoop } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/TheScoop';
import {
  ResourcePageSection,
  ScoopStatBadge,
  ScoopStatItem,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { useGetContentNuggetsByIdsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { Box, Button, Chip, SxProps, Theme } from '@mui/material';

import { QualificationsPanel } from './molecules/QualificationsPanel';
import { ResourcesSection } from './molecules/ResourcesSection';

const STRINGS = appStrings.modules.internship;

const formatDate = (value?: string): string => {
  if (!value) return '';

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

const formatCompensation = (compRange?: string, compEst?: string | number): string => {
  if (compRange && compRange.trim()) return compRange;
  if (compEst === undefined || compEst === null) return '';

  const compEstNum = typeof compEst === 'number' ? compEst : Number(compEst);

  if (Number.isNaN(compEstNum)) return '';

  // comp_est is a single decimal that's hourly for low values and annualized for high ones —
  // the source system doesn't normalize, so split on $1k.
  if (compEstNum < 1000) {
    return `~$${compEstNum.toFixed(2)}/hr`;
  }

  return `~$${Math.round(compEstNum).toLocaleString('en-US')}/yr`;
};

type InternshipPageProps = Readonly<{
  internship: Internship;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const InternshipPage = ({
  internship,
  handleBookmark,
  hasBookmarked,
}: InternshipPageProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const companyName = internship.employer?.name ?? internship.company;
  const websiteUrl = internship.employer?.corporate_website ?? internship.company_website;
  const applicationUrl = internship.application_url;

  const employerNavigate = internship.employer?.slug
    ? () => navigate(`${APP_ROUTE.EMPLOYER}/${internship.employer?.slug}`)
    : undefined;
  const employerBadgeIconConfig: NuggetBadgeProps['iconConfig'] = internship.employer
    ?.logo_media_url
    ? { mode: 'avatar', avatarUrl: internship.employer.logo_media_url }
    : { mode: 'appIcon', icon: 'Building06' };
  const titleExtraNode = internship.employer ? (
    <Box
      id="internship-employer-link"
      sx={titleExtraWrapperSx(!!employerNavigate)}
      onClick={employerNavigate}
    >
      <NuggetBadge label={internship.employer.name} iconConfig={employerBadgeIconConfig} />
    </Box>
  ) : companyName ? (
    <Chip
      id="internship-company-name-chip"
      variant="pill"
      size="md"
      type="leading"
      label={companyName}
      sx={companyNameChipSx}
    />
  ) : undefined;

  const sponsorBadges = [internship.sponsor, internship.validator]
    .filter((employer): employer is Employer => !!employer)
    .map<ScoopStatBadge>((employer) => ({
      id: `internship-sponsor-${employer.id}-badge`,
      label: employer.name,
      iconConfig: employer.logo_media_url
        ? { mode: 'avatar', avatarUrl: employer.logo_media_url }
        : { mode: 'appIcon', icon: 'Building06' },
      onClick: employer.slug ? () => navigate(`${APP_ROUTE.EMPLOYER}/${employer.slug}`) : undefined,
    }));

  const scoopSummaryItems = [
    {
      description: internship.ai_description || '',
    },
  ];

  const scoopStatItems: ScoopStatItem[] = [
    {
      kind: 'text',
      title: STRINGS.scoop.applicationDeadline,
      iconName: 'CalendarDate',
      text: formatDate(internship.job_expires_at),
    },
    {
      kind: 'text',
      title: STRINGS.scoop.location,
      iconName: 'MarkerPin01',
      text: internship.location || '',
    },
    {
      kind: 'text',
      title: STRINGS.scoop.compensation,
      iconName: 'PiggyBank02',
      text: formatCompensation(internship.comp_range, internship.comp_est),
    },
    ...(sponsorBadges.length > 0
      ? [
          {
            kind: 'badges' as const,
            title: STRINGS.scoop.sponsors,
            badges: sponsorBadges,
          },
        ]
      : []),
  ];

  const hasQualifications =
    !!internship.requirements || !!internship.industry || (internship.careerPaths?.length ?? 0) > 0;

  const resourceIds = internship.content_nugget_ids ?? [];
  const { data: resources = [], isLoading: resourcesLoading } = useGetContentNuggetsByIdsQuery(
    resourceIds,
    { skip: resourceIds.length === 0 },
  );

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'the-scoop',
      title: STRINGS.sections.theScoop,
      hideHeader: true,
      component: <TheScoop summaryItems={scoopSummaryItems} statItems={scoopStatItems} />,
    },
    hasQualifications && {
      id: 'qualifications',
      title: STRINGS.qualifications.title,
      description: STRINGS.qualifications.description,
      headerRightComponents: [
        <LubavButton
          key="internship-qualifications-lubav-btn"
          id="internship-qualifications-lubav-btn"
          label="Is this role a good fit for me?"
          promptButtonId="internship_is_role_a_good_fit_for_me"
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: (
        <QualificationsPanel
          requirements={internship.requirements}
          industry={internship.industry}
          industryName={internship.industry_name}
          careerPaths={internship.careerPaths}
        />
      ),
    },
    (resources.length > 0 || (resourcesLoading && resourceIds.length > 0)) && {
      id: 'resources',
      title: STRINGS.resources.title,
      description: STRINGS.resources.description,
      headerRightComponents: [
        <LubavButton
          key="internship-resources-lubav-btn"
          id="internship-resources-lubav-btn"
          label="Help me get started"
          prompt="Look at the Resources section on this page and pick the single resource I should start with and a brief explanation of why."
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: <ResourcesSection resources={resources} isLoading={resourcesLoading} />,
    },
  ].filter(Boolean) as ResourcePageSection[];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          title={internship.role_title}
          titleExtra={titleExtraNode}
          subtitle={getInternshipRoleLabel(internship.role_type)}
          titleIcon={{ icon: 'Briefcase01', backgroundColor: 'Brand/200' }}
          banner_media_url={internship.employer?.banner_media_url || undefined}
          nugget={internshipTransform(internship)}
          buttonGroup={[
            {
              id: 'internship-bookmark-btn',
              onClick: handleBookmark,
              icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
              tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
            },
            {
              id: 'internship-share-btn',
              onClick: async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                } catch (err) {
                  console.error('Clipboard write failed:', err);
                }
              },
              icon: 'Share06',
              tooltipOnClickText: 'Copied!',
              tooltipHoverText: commonAppStrings.modules.actionTabs.share,
            },
            {
              id: 'internship-add-to-task-btn',
              onClick: () => {
                dispatch(setNuggetToAdd(internshipTransform(internship)));
                dispatch(openPlaylistListStep());
              },
              icon: 'BookmarkAdd' as AppIcons,
              tooltipHoverText: commonAppStrings.modules.actionTabs.task,
            },
          ]}
          headerActions={
            <>
              {websiteUrl && (
                <Button
                  id="internship-company-website-btn"
                  hierarchy="Secondary gray"
                  size="md"
                  icon="Leading"
                  onClick={() => {
                    window.open(addHttpIfMissing(websiteUrl), '_blank');
                  }}
                  startIcon={
                    <AppIcon iconName="Link01" diameter="20px" color="Colors/Text/text-secondary" />
                  }
                >
                  {STRINGS.header.companyWebsite}
                </Button>
              )}
              {applicationUrl && (
                <Button
                  id="internship-apply-btn"
                  hierarchy="Primary"
                  size="md"
                  icon="Leading"
                  onClick={() => {
                    window.open(addHttpIfMissing(applicationUrl), '_blank');
                  }}
                  startIcon={
                    <AppIcon iconName="Share01" diameter="20px" color="Colors/Base/White" />
                  }
                >
                  {STRINGS.header.apply}
                </Button>
              )}
            </>
          }
        />
      }
      sections={PAGE_SECTIONS}
      showTabs={false}
      chipLabel={internship.role_title}
    />
  );
};

const titleExtraWrapperSx =
  (isClickable: boolean): SxProps<Theme> =>
  () => ({
    display: 'inline-flex',
    cursor: isClickable ? 'pointer' : 'default',
  });

const companyNameChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Base/Black'],
    ...typography['text-xs-medium'],
  },
});
